import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import AdminService from "../services/AdminService";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const AdViewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [ad, setAd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImageIdx, setActiveImageIdx] = useState(0);

    // Modals
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [extendDays, setExtendDays] = useState(30);

    const [actionLoading, setActionLoading] = useState("");

    const fetchAd = useCallback(async () => {
        setLoading(true);
        try {
            const res = await AdminService.getAdById(id);
            const data = res.data?.data || res.data;
            setAd(data);
        } catch (err) {
            console.error("Failed to fetch ad:", err);
            toast.error("Failed to load ad details");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchAd();
    }, [fetchAd]);

    const getAdStatus = () => {
        if (!ad) return "unknown";
        if (ad.blocked || ad.isBlocked) return "blocked";
        if (ad.sold || ad.isSold) return "sold";
        if (ad.expireDate && new Date(ad.expireDate) < new Date()) return "expired";
        if (ad.status === true || ad.status === "true") return "active";
        return "pending";
    };

    const getImages = () => {
        if (ad?.productImages && ad.productImages.length > 0) {
            return ad.productImages.map((img) => (typeof img === "string" ? img : img.imageUrl || img.url || img));
        }
        return [];
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    };

    const formatPrice = (price) => {
        if (!price && price !== 0) return "N/A";
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);
    };

    const getDaysRemaining = () => {
        if (!ad?.expireDate) return 0;
        const diff = new Date(ad.expireDate) - new Date();
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    };

    // Actions
    const handleAction = async (action, label) => {
        setActionLoading(action);
        try {
            switch (action) {
                case "approve":
                    if (!window.confirm("Approve this ad and make it live?")) {
                        setActionLoading("");
                        return;
                    }
                    await AdminService.approveAd(id);
                    break;
                case "block":
                    const isBlocked = adStatus === "blocked";
                    const blockMsg = isBlocked ? "Are you sure you want to unblock this ad?" : "Are you sure you want to block this ad?";
                    if (!window.confirm(blockMsg)) {
                        setActionLoading("");
                        return;
                    }
                    if (isBlocked) {
                        await AdminService.unblockAd(id);
                    } else {
                        await AdminService.blockAd(id);
                    }
                    break;
                case "delete":
                    if (!window.confirm("Are you sure you want to delete this ad?")) {
                        setActionLoading("");
                        return;
                    }
                    await AdminService.deleteAd(id);
                    toast.success("Ad deleted");
                    navigate("/admin/ads");
                    return;
                default:
                    break;
            }
            toast.success(`Ad ${label || action}d successfully`);
            fetchAd();
        } catch (err) {
            toast.error(`Failed to ${action} ad`);
        } finally {
            setActionLoading("");
        }
    };

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            toast.error("Please provide a rejection reason");
            return;
        }
        setActionLoading("reject");
        try {
            await AdminService.rejectAd(id, rejectReason);
            toast.success("Ad rejected");
            setShowRejectModal(false);
            setRejectReason("");
            fetchAd();
        } catch (err) {
            toast.error("Failed to reject ad");
        } finally {
            setActionLoading("");
        }
    };

    const handleExtend = async () => {
        if (extendDays <= 0) {
            toast.error("Enter a valid number of days");
            return;
        }
        setActionLoading("extend");
        try {
            await AdminService.extendAdExpiry(id, extendDays);
            toast.success(`Expiry extended by ${extendDays} days`);
            setShowExtendModal(false);
            setExtendDays(30);
            fetchAd();
        } catch (err) {
            toast.error("Failed to extend expiry");
        } finally {
            setActionLoading("");
        }
    };

    // Skeleton loading
    if (loading) {
        return (
            <div>
                <div className="admin-breadcrumb">
                    <Link to="/admin/ads">Ads</Link>
                    <i className="fa fa-angle-right" />
                    <span>Loading...</span>
                </div>
                <div className="admin-card">
                    <div className="admin-card-body">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="admin-skeleton-row">
                                <div style={{ flex: 1 }}>
                                    <div className="admin-skeleton admin-skeleton-text" />
                                    <div className="admin-skeleton admin-skeleton-text short" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!ad) {
        return (
            <div>
                <div className="admin-breadcrumb">
                    <Link to="/admin/ads">Ads</Link>
                    <i className="fa fa-angle-right" />
                    <span>Not Found</span>
                </div>
                <div className="admin-card">
                    <div className="admin-card-body" style={{ textAlign: "center", padding: 60 }}>
                        <i className="fa fa-exclamation-triangle" style={{ fontSize: 48, color: "#dc3545", marginBottom: 15 }} />
                        <h3>Ad Not Found</h3>
                        <p>The ad with ID #{id} was not found.</p>
                        <button className="admin-btn admin-btn-primary" onClick={() => navigate("/admin/ads")}>
                            <i className="fa fa-arrow-left" /> Back to Ads
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const adStatus = getAdStatus();
    const images = getImages();

    const statusConfig = {
        active: { label: "Active", className: "active", color: "#28a745" },
        pending: { label: "Pending", className: "pending", color: "#ffc107" },
        blocked: { label: "Blocked", className: "blocked", color: "#dc3545" },
        expired: { label: "Expired", className: "expired", color: "#6c757d" },
        sold: { label: "Sold", className: "sold", color: "#17a2b8" },
    };

    const currentStatus = statusConfig[adStatus] || statusConfig.pending;

    return (
        <div>
            {/* Breadcrumb */}
            <div className="admin-breadcrumb">
                <Link to="/admin/ads">Ads</Link>
                <i className="fa fa-angle-right" />
                <span>#{ad.productId || ad.id} — {ad.title}</span>
            </div>

            {/* Page Header */}
            <div className="admin-page-header">
                <h2>{ad.title}</h2>
                <div className="admin-page-actions">
                    <button className="admin-btn admin-btn-outline" onClick={() => navigate(`/admin/ads/edit/${id}`)}>
                        <i className="fa fa-pencil" /> Edit Ad
                    </button>
                    <button className="admin-btn admin-btn-outline" onClick={() => navigate("/admin/ads")}>
                        <i className="fa fa-arrow-left" /> Back to Ads
                    </button>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
                {/* Left Column */}
                <div>
                    {/* Image Gallery */}
                    {images.length > 0 && (
                        <div className="admin-card">
                            <div className="admin-card-body">
                                <div className="admin-gallery">
                                    <div className="admin-gallery-main">
                                        <img
                                            src={images[activeImageIdx]}
                                            alt={ad.title}
                                            onError={(e) => { e.target.src = "https://via.placeholder.com/600x400?text=Image+Error"; }}
                                        />
                                        {images.length > 1 && (
                                            <>
                                                <button className="admin-gallery-nav prev"
                                                    onClick={() => setActiveImageIdx((i) => (i === 0 ? images.length - 1 : i - 1))}>
                                                    <i className="fa fa-angle-left" />
                                                </button>
                                                <button className="admin-gallery-nav next"
                                                    onClick={() => setActiveImageIdx((i) => (i === images.length - 1 ? 0 : i + 1))}>
                                                    <i className="fa fa-angle-right" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {images.length > 1 && (
                                        <div className="admin-gallery-thumbs">
                                            {images.map((img, i) => (
                                                <img
                                                    key={i}
                                                    src={img}
                                                    alt={`Thumb ${i + 1}`}
                                                    className={activeImageIdx === i ? "active" : ""}
                                                    onClick={() => setActiveImageIdx(i)}
                                                    onError={(e) => { e.target.src = "https://via.placeholder.com/80x80?text=?"; }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Stats Row */}
                    <div className="admin-stats-row">
                        <div className="admin-stat-item">
                            <i className="fa fa-eye" />
                            <span>{ad.viewsCount || ad.viewCount || 0}</span>
                            <small>Views</small>
                        </div>
                        <div className="admin-stat-item">
                            <i className="fa fa-heart" />
                            <span>{ad.likesCount || ad.likeCount || 0}</span>
                            <small>Likes</small>
                        </div>
                        <div className="admin-stat-item">
                            <i className="fa fa-calendar" />
                            <span>{getDaysRemaining()}</span>
                            <small>Days Left</small>
                        </div>
                        <div className="admin-stat-item">
                            <i className="fa fa-image" />
                            <span>{images.length}</span>
                            <small>Images</small>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="admin-card">
                        <div className="admin-card-header"><h3>Description</h3></div>
                        <div className="admin-card-body">
                            <div style={{ lineHeight: 1.8, color: "#444", whiteSpace: "pre-wrap" }}>
                                {ad.description || "No description provided."}
                            </div>
                        </div>
                    </div>

                    {/* Ad Details Grid */}
                    <div className="admin-card">
                        <div className="admin-card-header"><h3>Ad Details</h3></div>
                        <div className="admin-card-body">
                            <div className="admin-detail-grid">
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Ad ID</span>
                                    <span className="admin-detail-value">#{ad.productId || ad.id}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Price</span>
                                    <span className="admin-detail-value" style={{ fontWeight: 700, color: "#2a89ba" }}>{formatPrice(ad.price)}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Category</span>
                                    <span className="admin-detail-value">{ad.subCategory?.parentCategory?.name || "N/A"} / {ad.subCategory?.name || "N/A"}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Slug</span>
                                    <span className="admin-detail-value" style={{ fontFamily: "monospace", fontSize: 12 }}>{ad.slug || "N/A"}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Status</span>
                                    <span className="admin-detail-value">
                                        <span className={`admin-badge ${currentStatus.className}`}>{currentStatus.label}</span>
                                    </span>
                                </div>
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Featured</span>
                                    <span className="admin-detail-value">{ad.featured ? "⭐ Yes" : "No"}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Created</span>
                                    <span className="admin-detail-value">{formatDate(ad.createdAt)}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Expires</span>
                                    <span className="admin-detail-value">{formatDate(ad.expireDate)}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Last Updated</span>
                                    <span className="admin-detail-value">{formatDate(ad.updatedAt)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact & Location */}
                    <div className="admin-card">
                        <div className="admin-card-header"><h3>Contact & Location</h3></div>
                        <div className="admin-card-body">
                            <div className="admin-detail-grid">
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Contact Name</span>
                                    <span className="admin-detail-value">{ad.contactName || "N/A"}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <span className="admin-detail-label">Phone</span>
                                    <span className="admin-detail-value" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        {ad.contactPhone || "N/A"}
                                        {ad.hideMobile && <span style={{ color: "#dc3545", fontSize: 11 }}>(Hidden from public)</span>}
                                    </span>
                                </div>
                                {ad.productAddresses && (
                                    <>
                                        <div className="admin-detail-row">
                                            <span className="admin-detail-label">Country</span>
                                            <span className="admin-detail-value">{ad.productAddresses.country || "N/A"}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <span className="admin-detail-label">State</span>
                                            <span className="admin-detail-value">{ad.productAddresses.state || "N/A"}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <span className="admin-detail-label">City</span>
                                            <span className="admin-detail-value">{ad.productAddresses.city || "N/A"}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <span className="admin-detail-label">Location</span>
                                            <span className="admin-detail-value">{ad.productAddresses.location || "N/A"}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <span className="admin-detail-label">Coordinates</span>
                                            <span className="admin-detail-value">{ad.productAddresses.lat ? `${ad.productAddresses.lat}, ${ad.productAddresses.lng}` : "Not set"}</span>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Leaflet Map */}
                            {ad.productAddresses?.lat && ad.productAddresses?.lng && (
                                <div style={{ height: 300, width: "100%", borderRadius: 8, overflow: "hidden", border: "1px solid #ddd", marginTop: 20 }}>
                                    <MapContainer
                                        center={[parseFloat(ad.productAddresses.lat), parseFloat(ad.productAddresses.lng)]}
                                        zoom={13}
                                        scrollWheelZoom={false}
                                        style={{ height: "100%", width: "100%" }}
                                    >
                                        <TileLayer
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        />
                                        <Marker position={[parseFloat(ad.productAddresses.lat), parseFloat(ad.productAddresses.lng)]} />
                                    </MapContainer>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Country Visibility */}
                    {ad.countryList && ad.countryList.length > 0 && (
                        <div className="admin-card">
                            <div className="admin-card-header"><h3>Country Visibility</h3></div>
                            <div className="admin-card-body">
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                    {ad.countryList.map((country, i) => (
                                        <span key={i} className="admin-badge" style={{ background: "#f0f3ff", color: "#2a89ba", border: "1px solid #d8dff7" }}>
                                            <i className="fa fa-globe" style={{ marginRight: 5 }} />{country}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Product Attributes */}
                    {ad.productAttributes && ad.productAttributes.length > 0 && (
                        <div className="admin-card">
                            <div className="admin-card-header"><h3>Attributes</h3></div>
                            <div className="admin-card-body">
                                <div className="admin-detail-grid">
                                    {ad.productAttributes.map((attr, i) => (
                                        <div key={i} className="admin-detail-row">
                                            <span className="admin-detail-label">{attr.name || attr.key}</span>
                                            <span className="admin-detail-value">{attr.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Column — Actions & Owner Info */}
                <div>
                    {/* Quick Actions */}
                    <div className="admin-card">
                        <div className="admin-card-header"><h3>Quick Actions</h3></div>
                        <div className="admin-card-body admin-action-panel">
                            <button className="admin-btn admin-btn-success" style={{ width: "100%" }}
                                onClick={() => handleAction("approve", "approve")}
                                disabled={actionLoading === "approve" || adStatus === "active"}>
                                {actionLoading === "approve" ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-check" />}{" "}
                                Approve
                            </button>
                            <button className="admin-btn admin-btn-warning" style={{ width: "100%" }}
                                onClick={() => setShowRejectModal(true)} disabled={adStatus === "blocked"}>
                                <i className="fa fa-ban" /> Reject
                            </button>
                            <button className="admin-btn admin-btn-outline" style={{ width: "100%" }}
                                onClick={() => handleAction("feature", ad.featured ? "unfeature" : "feature")}
                                disabled={actionLoading === "feature"}>
                                {actionLoading === "feature" ? <i className="fa fa-spinner fa-spin" /> :
                                    <i className={`fa ${ad.featured ? "fa-star" : "fa-star-o"}`} />}
                                {" "}{ad.featured ? "Remove Featured" : "Mark as Featured"}
                            </button>
                            <button className="admin-btn admin-btn-outline" style={{ width: "100%" }}
                                onClick={() => handleAction("block", adStatus === "blocked" ? "unblock" : "block")}
                                disabled={actionLoading === "block"}>
                                {actionLoading === "block" ? <i className="fa fa-spinner fa-spin" /> :
                                    <i className={`fa ${adStatus === "blocked" ? "fa-unlock" : "fa-lock"}`} />}
                                {" "}{adStatus === "blocked" ? "Unblock Ad" : "Block Ad"}
                            </button>
                            <button className="admin-btn admin-btn-outline" style={{ width: "100%" }}
                                onClick={() => setShowExtendModal(true)}>
                                <i className="fa fa-clock-o" /> Extend Expiry
                            </button>
                            <hr style={{ margin: "8px 0", border: "none", borderTop: "1px solid #eee" }} />
                            <button className="admin-btn admin-btn-danger" style={{ width: "100%" }}
                                onClick={() => handleAction("delete", "delete")}
                                disabled={actionLoading === "delete"}>
                                {actionLoading === "delete" ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-trash" />}
                                {" "}Delete Ad
                            </button>
                        </div>
                    </div>

                    {/* Owner Info */}
                    {ad.user && (
                        <div className="admin-card">
                            <div className="admin-card-header"><h3>Ad Owner</h3></div>
                            <div className="admin-card-body">
                                <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
                                    <div style={{
                                        width: 52, height: 52, borderRadius: "50%",
                                        background: "linear-gradient(135deg, #2a89ba, #3b82f6)",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        color: "#fff", fontSize: 20, fontWeight: 700
                                    }}>
                                        {(ad.user.name || "?").charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: 15 }}>{ad.user.name}</div>
                                        <div style={{ color: "#6c757d", fontSize: 12 }}>{ad.user.email}</div>
                                        {ad.user.phone && <div style={{ color: "#6c757d", fontSize: 12 }}>{ad.user.phone}</div>}
                                    </div>
                                </div>
                                <div style={{ marginTop: 15 }}>
                                    <button className="admin-btn admin-btn-outline admin-btn-sm" style={{ width: "100%" }}
                                        onClick={() => navigate(`/admin/users`)}>
                                        <i className="fa fa-user" /> View User Profile
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Featured Info */}
                    {ad.featured && (
                        <div className="admin-card" style={{ borderLeft: "4px solid #ebad1e" }}>
                            <div className="admin-card-body">
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                                    <span style={{ fontSize: 20 }}>⭐</span>
                                    <strong>Featured Ad</strong>
                                </div>
                                <p style={{ fontSize: 13, color: "#6c757d", margin: 0 }}>
                                    Featured until: {formatDate(ad.featuredExpDate)}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Reject Modal */}
            {showRejectModal && (
                <div className="admin-modal-overlay" onClick={() => setShowRejectModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3>Reject Ad</h3>
                            <button className="admin-modal-close" onClick={() => setShowRejectModal(false)}>×</button>
                        </div>
                        <div className="admin-modal-body">
                            <p>Rejecting: <strong>#{ad.productId || ad.id} — {ad.title}</strong></p>
                            <div className="admin-form-group">
                                <label>Rejection Reason *</label>
                                <textarea className="admin-form-control" rows="3"
                                    placeholder="Why is this ad being rejected?"
                                    value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                                    style={{ resize: "vertical" }} />
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn admin-btn-outline" onClick={() => setShowRejectModal(false)}>Cancel</button>
                            <button className="admin-btn admin-btn-danger" onClick={handleReject}
                                disabled={actionLoading === "reject"}>
                                {actionLoading === "reject" ? <><i className="fa fa-spinner fa-spin" /> Rejecting...</> : <><i className="fa fa-ban" /> Reject</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Extend Expiry Modal */}
            {showExtendModal && (
                <div className="admin-modal-overlay" onClick={() => setShowExtendModal(false)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3>Extend Ad Expiry</h3>
                            <button className="admin-modal-close" onClick={() => setShowExtendModal(false)}>×</button>
                        </div>
                        <div className="admin-modal-body">
                            <p>Current expiry: <strong>{formatDate(ad.expireDate)}</strong></p>
                            <div className="admin-form-group">
                                <label>Extend by (days)</label>
                                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                                    {[7, 15, 30, 60, 90].map((d) => (
                                        <button key={d} type="button"
                                            className={`admin-btn ${extendDays === d ? "admin-btn-primary" : "admin-btn-outline"} admin-btn-sm`}
                                            onClick={() => setExtendDays(d)}>
                                            {d}d
                                        </button>
                                    ))}
                                </div>
                                <input type="number" className="admin-form-control" value={extendDays}
                                    onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                                    min="1" max="365" style={{ marginTop: 10 }} />
                            </div>
                            <p style={{ fontSize: 13, color: "#6c757d" }}>
                                New expiry: <strong>{new Date(new Date(ad.expireDate || Date.now()).getTime() + extendDays * 86400000).toLocaleDateString()}</strong>
                            </p>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn admin-btn-outline" onClick={() => setShowExtendModal(false)}>Cancel</button>
                            <button className="admin-btn admin-btn-primary" onClick={handleExtend}
                                disabled={actionLoading === "extend"}>
                                {actionLoading === "extend" ? <><i className="fa fa-spinner fa-spin" /> Extending...</> : <><i className="fa fa-clock-o" /> Extend</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdViewPage;
